import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const PublicNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  
  // Scroll efekti
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 60) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Sayfa dışında bir yere tıklayınca menüyü kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.mobile-menu-container')) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  
  // Aktif sayfa kontrolü
  const isActive = (path) => {
    return location.pathname === path;
  };
  
  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{ 
        height: scrolled ? '70px' : '80px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
      }}
    >
      {/* Navbar arka planı */}
      <div 
        className={`absolute inset-0 transition-all duration-500 border-b ${
          scrolled 
            ? 'bg-white border-gray-300 shadow-md' 
            : 'bg-white border-gray-300 shadow-md'
        }`}
      >
        {/* Alt çizgi highlight */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500"></div>
      </div>
      
      <div className="container mx-auto h-full px-6 relative z-10">
        <div className="flex items-center justify-between h-full">
          {/* Logo */}
          <Link to="/" className="group flex items-center">
            <div className="flex items-center bg-white rounded-xl px-4 py-2 shadow-md transition-all duration-300 border-2 border-purple-500">
              <span className="text-3xl mr-3 text-purple-600 group-hover:animate-pulse">♪</span>
              <span className="text-2xl font-bold text-purple-700">
                İYTE Müzik
              </span>
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <NavLink to="/" isActive={isActive('/')}>Ana Sayfa</NavLink>
            <NavLink to="/about" isActive={isActive('/about')}>Hakkımızda</NavLink>
            <NavLink to="/events" isActive={isActive('/events')}>Etkinlikler</NavLink>
            <NavLink to="/contact" isActive={isActive('/contact')}>İletişim</NavLink>
            {/* Login Button (Desktop) */}
            <Link 
              to="/login" 
              className="px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium hover:from-purple-700 hover:to-indigo-700 transition-colors duration-300 shadow-md flex items-center justify-center"
            >
              <button className="flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                Giriş Yap
              </button>
            </Link>
            
            
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 rounded-lg focus:outline-none transition-colors duration-300 ${
                isOpen 
                  ? 'bg-purple-100 text-purple-600' 
                  : 'text-gray-700 hover:bg-purple-50 hover:text-purple-600'
              }`}
              aria-label="Toggle menu"
            >
              {!isOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Navigation */}
      <div 
        className={`mobile-menu-container md:hidden fixed left-0 right-0 transition-all duration-300 ${
          isOpen 
            ? 'opacity-100 visible top-[80px]' 
            : 'opacity-0 invisible top-[60px]'
        }`}
        style={{ 
          boxShadow: isOpen ? '0 5px 15px rgba(0, 0, 0, 0.1)' : 'none',
          maxHeight: isOpen ? '100vh' : '0',
          overflow: 'hidden'
        }}
      >
        <div className="bg-white border-t border-gray-200 px-6 pb-6 pt-4 space-y-1">
          <MobileNavLink to="/" isActive={isActive('/')} onClick={() => setIsOpen(false)}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Ana Sayfa
          </MobileNavLink>
          
          <MobileNavLink to="/about" isActive={isActive('/about')} onClick={() => setIsOpen(false)}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Hakkımızda
          </MobileNavLink>
          
          <MobileNavLink to="/events" isActive={isActive('/events')} onClick={() => setIsOpen(false)}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Etkinlikler
          </MobileNavLink>
          
          <MobileNavLink to="/contact" isActive={isActive('/contact')} onClick={() => setIsOpen(false)}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            İletişim
          </MobileNavLink>
          
          {/* Login Button (Mobile) */}
          <div className="pt-4 mt-4 border-t border-gray-200">
            <Link 
              to="/login" 
              onClick={() => setIsOpen(false)}
              className="block w-full"
            >
              <button className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium hover:from-purple-700 hover:to-indigo-700 transition-colors duration-300 shadow-md flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                Giriş Yap
              </button>
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Desktop Nav Link
const NavLink = ({ to, isActive, children }) => {
  return (
    <Link 
      to={to} 
      className={`px-4 py-2 rounded-lg transition-all duration-300 relative group ${
        isActive 
          ? 'bg-purple-100 text-purple-700 font-medium' 
          : 'text-gray-700 hover:text-purple-700 hover:bg-purple-50'
      }`}
    >
      <span className="relative z-10 text-sm uppercase tracking-wider font-medium">{children}</span>
      
      {/* Active indicator line */}
      <span 
        className={`absolute bottom-0 left-0 right-0 mx-auto w-12 h-0.5 bg-gradient-to-r from-purple-500 to-indigo-500 transform transition-all duration-300 ${
          isActive 
            ? 'opacity-100 scale-x-100' 
            : 'opacity-0 scale-x-0 group-hover:opacity-70 group-hover:scale-x-75'
        }`}
      ></span>
    </Link>
  );
};

// Mobile Nav Link
const MobileNavLink = ({ to, isActive, onClick, children }) => {
  return (
    <Link 
      to={to} 
      onClick={onClick}
      className={`flex items-center py-3 px-4 rounded-lg transition-all duration-300 ${
        isActive 
          ? 'bg-purple-100 text-purple-700 font-medium' 
          : 'text-gray-700 hover:bg-purple-50 hover:text-purple-700'
      }`}
    >
      {children}
    </Link>
  );
};

export default PublicNavbar;